import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { useAnalytics } from '~/analytics/useAnalytics';
import { useWhatsNew } from '~/components/whatsNew/useWhatsNew';
import { WhatsNewEntryTitle } from '~/components/whatsNew/WhatsNewEntryTitle';
import { type ChangelogEntry } from '~/data/schemas/changelog';
import { SURFACE_RADIUS } from '~/theme';

export function WhatsNewCard() {
  const { unreadEntries, dialogEntries, markSeen } = useWhatsNew();

  // The dialog goes first; the card waits until it is closed.
  if (unreadEntries.length === 0 || dialogEntries.length > 0) return null;

  return <UnreadUpdatesCard unreadEntries={unreadEntries} markSeen={markSeen} />;
}

function UnreadUpdatesCard({
  unreadEntries,
  markSeen,
}: {
  unreadEntries: ChangelogEntry[];
  markSeen: () => void;
}) {
  const { track } = useAnalytics();
  const [page, setPage] = useState({ newestId: '', currentId: '', index: 0 });
  // Ids of the entries this card has displayed. Seen is stored as one date, so the card
  // keeps its own record: same-day entries are told apart by id, not by date.
  const [shownIds, setShownIds] = useState<ReadonlySet<string>>(new Set());
  const nextRef = useRef<HTMLButtonElement>(null);
  const dismissRef = useRef<HTMLButtonElement>(null);
  const backHadFocusRef = useRef(false);

  const total = unreadEntries.length;
  const newestId = unreadEntries[0].id;
  // Start over when a refresh brings in a newer update, so Done can't mark it seen unread.
  // Otherwise follow the entry by id, so an entry a refresh inserts above it doesn't shift
  // what the reader is looking at. If that entry has gone (another tab marked it seen), stay
  // at the same place in the list rather than jumping back to the first entry.
  const found =
    page.newestId === newestId
      ? unreadEntries.findIndex((entry) => entry.id === page.currentId)
      : -1;
  const position =
    found >= 0 ? found : page.newestId === newestId ? Math.min(page.index, total - 1) : 0;
  const current = unreadEntries[position];
  const goTo = (index: number) => setPage({ newestId, currentId: unreadEntries[index].id, index });

  // Record the entry as it is displayed. Setting state during render re-renders before anything
  // is painted, so the button label below never shows a state that lacks the current entry.
  if (!shownIds.has(current.id)) setShownIds(new Set(shownIds).add(current.id));

  const firstUnshown = unreadEntries.findIndex((entry) => !shownIds.has(entry.id));
  const allShown = firstUnshown === -1;
  const isLast = position === total - 1;
  // On the last entry with others never shown (a refresh inserted one above), the primary
  // button goes to the first of them instead of finishing.
  const finishes = isLast && allShown;

  const handleEntryClick = (entry: ChangelogEntry) => {
    track('whats_new_entry_clicked', { entry_id: entry.id, source: 'card' });
    // Opening one entry's link must not hide the entries still waiting behind it.
    if (allShown) markSeen();
  };

  const handleDismiss = () => {
    track('whats_new_card_dismissed', { unread_count: unreadEntries.length });
    markSeen();
  };

  // Back disappears on the first entry, whether the reader clicked it or a refresh restarted
  // paging. When it takes focus with it, hand focus to a control that stays, so a keyboard
  // user isn't dropped onto the page body behind this fixed card.
  const hasBack = total > 1 && position > 0;
  useEffect(() => {
    if (hasBack || !backHadFocusRef.current) return;
    backHadFocusRef.current = false;
    if (document.activeElement && document.activeElement !== document.body) return;
    (nextRef.current ?? dismissRef.current)?.focus();
  }, [hasBack]);

  return (
    <Paper
      role="status"
      aria-label="New updates"
      elevation={8}
      sx={(theme) => ({
        position: 'fixed',
        left: 16,
        // On desktop, clear the sidebar's version label so it stays in support screenshots.
        bottom: { xs: 16, sm: 40 },
        width: 'min(360px, calc(100vw - 32px))',
        maxHeight: { xs: 'calc(100vh - 32px)', sm: 'calc(100vh - 56px)' },
        overflowY: 'auto',
        p: 2,
        borderRadius: `${SURFACE_RADIUS}px`,
        // The desktop sidebar's nav wrapper stacks at `drawer + 2`; sit above it, below modals.
        zIndex: { xs: theme.zIndex.drawer - 1, sm: theme.zIndex.drawer + 3 },
      })}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" color="primary" sx={{ lineHeight: 1.5 }}>
            {total === 1 ? "What's new" : `What's new · ${position + 1} of ${total}`}
          </Typography>
          <WhatsNewEntryTitle
            key={current.id}
            entry={current}
            onClick={() => handleEntryClick(current)}
          />
          <Typography variant="body2" color="text.secondary">
            {current.summary}
          </Typography>
          {total > 1 && (
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', pt: 0.5 }}>
              {position > 0 && (
                <Button
                  size="small"
                  onClick={() => goTo(position - 1)}
                  onFocus={() => {
                    backHadFocusRef.current = true;
                  }}
                  onBlur={(event) => {
                    // Focus moving to another control; removal from the DOM has no target.
                    if (event.relatedTarget) backHadFocusRef.current = false;
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                ref={nextRef}
                size="small"
                variant="contained"
                onClick={
                  finishes ? handleDismiss : () => goTo(isLast ? firstUnshown : position + 1)
                }
              >
                {finishes ? 'Done' : 'Next'}
              </Button>
            </Stack>
          )}
        </Stack>
        <IconButton
          ref={dismissRef}
          size="small"
          aria-label="Dismiss"
          onClick={handleDismiss}
          sx={{ mt: -0.5, mr: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}
